import LocalStorageHelper from "helpers/LocalStorageHelper";
import { createContext, useCallback, useMemo, useState } from "react";

export type LocalStorageKey = (typeof LocalStorageHelper.keys)[keyof typeof LocalStorageHelper.keys];

export interface OnBoardingType {
  getOpen: (key: LocalStorageKey, fallbackIsOpenValue: boolean) => boolean;
  setOpen: (key: LocalStorageKey, isOpen: boolean) => void;
}

export const OnBoardingContext = createContext<OnBoardingType | undefined>(undefined);
OnBoardingContext.displayName = "OnBoardingContext";

interface OnBoardingProviderProps {
  children?: React.ReactNode;
}

function OnBoardingProvider(props: OnBoardingProviderProps) {
  const [onBoardingOpen, setOnBoardingOpen] = useState<{ [key: string]: boolean | undefined }>({});

  const getOpen = useCallback(
    (key: LocalStorageKey, fallbackIsOpenValue: boolean) => {
      return onBoardingOpen[key] ?? Boolean(LocalStorageHelper.get(key, fallbackIsOpenValue));
    },
    [onBoardingOpen]
  );

  const setOpen = useCallback((key: LocalStorageKey, isOpen: boolean) => {
    LocalStorageHelper.set(key, isOpen);
    setOnBoardingOpen((prev) => ({ ...prev, [key]: isOpen }));
  }, []);

  const contextValue: OnBoardingType = useMemo(() => ({ getOpen, setOpen }), [getOpen, setOpen]);

  return <OnBoardingContext.Provider value={contextValue}>{props.children}</OnBoardingContext.Provider>;
}

export default OnBoardingProvider;
